// File: src/components/HeaderControls.js
// The pieces every page header is built from.
//
// The public header and the admin header need the same language and country
// pickers and the same button styling. They used to hold a copy each - two
// hamburgers with two hand-built country popovers - and had already drifted
// apart by the time anyone looked.

import React, { useId } from 'react';
import { Globe, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCountry } from '../contexts/CountryContext';
import CountryOptions from './CountryOptions';

// Tailwind cannot see class names built at runtime, so each state is written
// out in full rather than assembled from fragments.
const BUTTON_BASE = 'px-3 py-2 rounded-lg font-semibold flex items-center gap-2 whitespace-nowrap';

export const HEADER_BUTTON = `${BUTTON_BASE} bg-paper-alt hover:opacity-80 text-ink`;
export const HEADER_BUTTON_ACTIVE = `${BUTTON_BASE} bg-accent text-accent-ink`;
export const HEADER_BUTTON_DANGER =
  `${BUTTON_BASE} bg-paper-alt hover:opacity-80 text-red-700 dark:text-red-400`;

// Both pickers are built the same way: icon, a visible label naming what the
// control chooses, then the current value in the select itself.
//
// The label is the whole point. Side by side and unlabelled, the two boxes read
// "Suomi" and "Finland" - two dropdowns saying nearly the same word, and no way
// to tell which one changes the site's language and which one filters the races.
// Naming them costs a little width and removes the guesswork.
const CONTROL_BOX =
  'flex items-center gap-2 pl-3 bg-paper-alt border border-line rounded-lg text-ink';
const CONTROL_LABEL = 'text-sm text-ink-muted whitespace-nowrap';
const CONTROL_SELECT = 'py-2 pr-3 bg-transparent text-ink font-semibold focus:outline-none';

/**
 * Language picker.
 *
 * A globe, not a flag. Flags are countries: Swedish is spoken in two of them and
 * Finland has two official languages, so a flag on a language control tells the
 * visitor the wrong thing. Names are endonyms - "Suomi", never "Finnish" - and
 * each option carries its own lang so a screen reader reads "Svenska" with
 * Swedish pronunciation rather than sounding it out in Finnish.
 */
export function LanguageSelect() {
  const { t, language, changeLanguage, availableLanguages } = useLanguage();
  // A page can hold more than one header control (the account page has its own),
  // so the label's id has to be unique per instance rather than a fixed string.
  const id = useId();

  return (
    <div className={CONTROL_BOX}>
      <Globe className="w-4 h-4 text-ink-muted flex-shrink-0" aria-hidden="true" />
      <label htmlFor={id} className={CONTROL_LABEL}>{t('language')}</label>
      <select
        id={id}
        value={language}
        onChange={(e) => changeLanguage(e.target.value)}
        className={CONTROL_SELECT}
      >
        {availableLanguages.map(lang => (
          <option key={lang.code} value={lang.code} lang={lang.code}>
            {lang.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Country picker - which country's series and races to show.
 *
 * Flags belong here, because here it is the country being chosen. The emoji
 * flag rather than the flag-icons CSS class: a <span> does not render inside an
 * <option>, and a native select is worth more than a hand-built dropdown of
 * twenty countries.
 */
export function CountrySelect() {
  const { t } = useLanguage();
  const { currentCountry, changeCountry } = useCountry();
  const id = useId();

  return (
    <div className={CONTROL_BOX}>
      <MapPin className="w-4 h-4 text-ink-muted flex-shrink-0" aria-hidden="true" />
      <label htmlFor={id} className={CONTROL_LABEL}>{t('country')}</label>
      <select
        id={id}
        value={currentCountry.code}
        onChange={(e) => changeCountry(e.target.value)}
        className={CONTROL_SELECT}
      >
        <CountryOptions />
      </select>
    </div>
  );
}
